/**
 * Ajouter un repas — trois modes de saisie, une seule feuille.
 *
 * Le formulaire était **déplié en permanence** au bas de l'écran : un sélecteur de type,
 * une zone de photo, une description et trois pas-à-pas, tout le temps, qu'on vienne
 * photographier son assiette ou taper trois nombres lus sur un emballage. Il demandait
 * donc de traverser ce dont on n'avait pas besoin pour atteindre ce qu'on voulait.
 *
 * Une feuille, et **le mode d'abord** : une description, un plat composé de ses
 * ingrédients, ou les valeurs à la main. Le mode ne change pas ce qui est enregistré — un
 * repas reste un repas — il change ce que la feuille demande et ce qu'elle propose
 * d'estimer.
 *
 * ## La photo s'est arrêtée (`NUT-22`)
 *
 * Deux modes la demandaient. Ils ont été retirés parce qu'ils ne servaient plus, pas
 * parce qu'ils fonctionnaient mal — la réduction à 1600 px, le `413` du reverse-proxy et
 * le `accept` sans HEIC avaient tous été payés une fois. **Les repas déjà photographiés
 * gardent leur image** : la vignette du journal et la fiche la montrent toujours, et le
 * serveur accepte encore une image à l'estimation. Ne plus pouvoir en ajouter n'est pas
 * une raison de cacher celles qui existent.
 *
 * ## Ce qui n'a pas bougé, et pourquoi
 *
 * **Une valeur proposée n'est pas une mesure.** L'estimation arrive dans un `AiBlock`, se
 * pose dans des pas-à-pas marqués `proposed`, et la marque disparaît dès qu'on retouche.
 * C'est la seule façon dont le projet le dit, et elle n'est pas redite ici autrement.
 *
 * **Rien n'est écrit avant le dernier appui.** L'estimation ne touche pas au stockage, et
 * les quatre portes de sortie de `Sheet` ferment sans rien laisser — à n'importe quelle
 * étape, y compris pendant l'attente.
 *
 * ## Aujourd'hui ou hier (M-01)
 *
 * Le dîner oublié se rattrape le lendemain matin. La feuille propose les deux jours que
 * le serveur sert, **aujourd'hui par défaut**, et renvoie la date reçue : elle ne calcule
 * ni la veille ni une heure. Le jour vit dans `values`, donc dans le brouillon ; il
 * survit à « Changer de mode », qui change la façon de noter, pas le repas qu'on note.
 *
 * ## Les favoris en tête (M-23)
 *
 * Le petit-déjeuner de tous les jours est le repas le plus court à noter, et il était le
 * plus loin : « rejouer » vivait sur la page, sous l'historique, à 2 800 px du haut. Il
 * ouvre maintenant la feuille, **avant** les trois modes, dans une bande qui défile : un
 * appui rejoue le favori au jour choisi et ferme la feuille. **Sans confirmation** — c'est
 * une addition, et elle se défait par la suppression du repas. L'ordre est celui que le
 * serveur sert ; la feuille ne trie rien.
 *
 * Le jour se choisit donc **dès cette étape** : un favori rejoué n'a pas de formulaire où
 * le dire ensuite. Le segment reste aussi en tête du formulaire, sur le même état.
 */

import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AiBlock,
  Button,
  Chip,
  ChipStrip,
  Field,
  Segmented,
  Sheet,
  SheetRow,
  Skeleton,
  Stepper,
} from '@/components/ui';
import { useAiStatus } from '@/features/ai/useAiStatus';
import {
  nutritionApi,
  type Composition,
  type Favorite,
  type Ingredient,
  type MealDay,
  type MealEstimate,
  type MealFormValues,
  type Product,
} from '@/features/nutrition/api';
import { ApiError } from '@/lib/api';
import { cx } from '@/lib/cx';
import { longDate } from '@/lib/format';
import { useToast } from '@/lib/toast';

import styles from '../Nutrition.module.css';
import { estimateSentence } from './estimate';
import { CompositionTotal, IngredientTable } from './Ingredients';
import {
  describedIngredient,
  emptyIngredient,
  ingredientFromCatalogue,
  ingredientFromProduct,
  isBlank,
  toLines,
  type IngredientDraft,
} from './ingredient-draft';
import {
  clearDraft,
  readDraft,
  writeDraft,
  type Macro,
  type MealDraft,
  type MealMode,
} from './meal-draft';
import { FoodDetail } from './FoodDetail';
import { NUTRIENTS } from './nutrients';
import { PickStep } from './PickStep';
import { ScanStep } from './ScanStep';

/* `Macro` et `MealMode` vivent dans `meal-draft.ts` : c'est lui qui doit les reconnaître
   dans du texte relu, et deux déclarations de la même liste finiraient par diverger. */

/** Les trois modes de saisie, dans l'ordre où ils sont proposés. */
const MODES: { value: MealMode; label: string; hint: string }[] = [
  { value: 'texte', label: 'Description', hint: 'les macros sont estimées' },
  { value: 'compose', label: 'Repas composé', hint: 'des aliments et leurs poids' },
  { value: 'manuel', label: 'Valeurs à la main', hint: 'protéines, calories, fibres…' },
];

/**
 * Le repos de frappe après lequel le total se redemande (`NUT-24`).
 *
 * Une demi-seconde : au-dessus, le total traîne derrière le doigt et on croit l'avoir
 * raté ; en dessous, taper « 180 » lance trois requêtes là où une suffit.
 */
const TOTAL_DELAY_MS = 500;

/**
 * Les deux valeurs qui attendent derrière « Plus de valeurs » (`NUT-25`).
 *
 * Cinq pas-à-pas faisaient ~750 px de formulaire avant le bouton d'enregistrement, et ces
 * deux-là ne se connaissent presque jamais sans emballage sous les yeux. Elles ne sont pas
 * **retirées** — `NUT-16` a coûté assez cher pour qu'on ne les oublie pas une seconde fois,
 * et elles partent toujours quand elles sont remplies. Ce qui change est ce qu'on traverse
 * pour atteindre le bouton.
 *
 * Des clés et non une seconde liste : les libellés, les pas et les modes de saisie restent
 * dans `NUTRIENTS`, qui est la seule énumération du dépôt.
 */
const LATER: readonly Macro[] = ['saturated_fat_g', 'fiber_g'];

/** Les deux modes qui n'appellent aucun modèle, et restent donc offerts sans clé. */
const OFFLINE_MODES: readonly MealMode[] = ['manuel', 'compose'];

/** Le mode demande-t-il une description ? Elle est ce que l'estimation lit. */
function wantsText(mode: MealMode): boolean {
  return mode === 'texte';
}

/** Le mode passe-t-il par une estimation ? Les deux derniers, non — c'est tout leur sens. */
function wantsEstimate(mode: MealMode): boolean {
  return !OFFLINE_MODES.includes(mode);
}

/**
 * Ce que la feuille montre : son formulaire, ou l'une des deux surfaces qui la prennent.
 *
 * La fiche porte la **clé** de sa ligne et non la ligne : ce qui est à l'écran doit rester
 * la ligne vivante, pas une photographie prise à l'ouverture.
 */
type Step =
  | { kind: 'form' }
  /** Le choix d'un chemin pour ajouter un aliment : catalogue, code-barres, main (`NUT-23`). */
  | { kind: 'pick' }
  | { kind: 'scan' }
  | { kind: 'food'; key: string };

const EMPTY: MealFormValues = {
  meal_type: '',
  comment: '',
  protein_g: '',
  added_sugar_g: '',
  calories: '',
  saturated_fat_g: '',
  fiber_g: '',
  source: 'manual',
  day: '',
};

export function MealSheet({
  open,
  onClose,
  onSaved,
  suggested,
  types,
  ingredients,
  days,
  initialDay,
  initialBarcode,
  favorites,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  suggested: string;
  types: string[];
  /** Catalogue d'ingrédients, servi avec le reste de l'écran (`NUT-12`). */
  ingredients: Ingredient[];
  /** Les jours où le repas peut être noté, servis avec l'écran (M-01). */
  days: MealDay[];
  /**
   * Le jour imposé par l'adresse — `/nutrition?ajout=hier`, où mène le parcours du matin.
   * Une date servie ; il l'emporte sur celui du brouillon, puisque c'est lui qu'on est venu
   * noter.
   */
  initialDay?: string | undefined;
  /**
   * Le code-barres d'un produit que personne ne connaît — `?code=`, où mène « Ajouter cet
   * aliment à la main » depuis le scan de « Noter » (`NUT-28`). La feuille s'ouvre sur le
   * repas composé et une **ligne à décrire** qui porte ce code.
   */
  initialBarcode?: string | undefined;
  /** Les favoris, dans l'ordre servi (M-23). Vide : la bande n'est pas dessinée. */
  favorites: readonly Favorite[];
}) {
  const { notify } = useToast();
  const ai = useAiStatus();

  const [mode, setMode] = useState<MealMode | null>(null);
  // Le jour de l'adresse se pose **à l'initialisation** : l'écran remonte la feuille
  // (`key`) quand l'adresse en impose un, plutôt que de le lui écrire dans un effet.
  const [values, setValues] = useState<MealFormValues>(() =>
    initialDay === undefined ? EMPTY : { ...EMPTY, day: initialDay },
  );
  const [error, setError] = useState<ApiError | null>(null);
  // « Plus de valeurs » : demandé du doigt. Ce que la saisie contient l'ouvre aussi, et
  // c'est calculé au rendu — voir `showLater`.
  const [laterOpen, setLaterOpen] = useState(false);

  // La feuille tient-elle déjà une saisie ? Une référence et non un état : l'effet de
  // reprise ne doit se rejouer qu'à l'ouverture, pas à chaque frappe.
  const live = useRef(false);
  // Le code de l'adresse ne se pose **qu'une fois** par montage : l'écran remonte la
  // feuille quand l'adresse en porte un autre (`key`), et rouvrir la même ne doit pas
  // ajouter une seconde ligne pour le même produit.
  const posted = useRef(false);
  /*
   * Chaque estimation demandée porte un numéro ; une réponse qui arrive après un
   * « Annuler », un enregistrement ou un changement de mode n'a plus de feuille où se
   * poser, et serait apparue dans le repas suivant (audit du 17 octobre, fiche 16).
   */
  const generation = useRef(0);
  /** L'enregistrement demandé alors qu'une estimation attend encore sa réponse (fiche 15). */
  const [asking, setAsking] = useState(false);

  // Ce que le modèle a proposé, et lesquelles de ces valeurs sont encore les siennes.
  // Deux états et non un : une valeur retouchée cesse d'être une proposition, mais
  // l'estimation reste affichée — elle explique d'où vient ce qui est dans les champs.
  const [estimate, setEstimate] = useState<MealEstimate | null>(null);
  const [proposed, setProposed] = useState<Macro[]>([]);

  // Le repas composé : ses lignes, et le total que le serveur en a tiré.
  //
  // **Vide au départ** (`NUT-14`). Une ligne vierge posée d'avance mettait cinq champs
  // entre l'ouverture du mode et le geste qui compte, et il fallait la traverser pour
  // atteindre le scan.
  const [rows, setRows] = useState<IngredientDraft[]>([]);
  const [total, setTotal] = useState<Composition | null>(null);
  // L'échec du total, tenu à part de celui du formulaire : voir `computeTotal`.
  const [totalError, setTotalError] = useState<string | null>(null);

  // Les surfaces qui prennent la feuille et la rendent : le scan (`NUT-13`) et la fiche
  // d'un aliment (`NUT-14`). Des étapes et non des `Sheet` imbriquées — le raisonnement
  // complet est en tête de `ScanStep.tsx`.
  //
  // Un seul état pour les deux : deux booléens auraient permis d'être dans les deux à la
  // fois, ce qui n'a pas de sens et se serait vu un jour à l'écran.
  //
  // **Hors du brouillon, délibérément.** Rouvrir la feuille doit rendre la saisie, pas
  // rallumer une caméra que personne n'a redemandée ni rouvrir une fiche.
  const [step, setStep] = useState<Step>({ kind: 'form' });
  /**
   * Le favori touché pour un autre jour qu'aujourd'hui, qui attend son type. Un favori ne
   * porte pas le sien, et celui de l'heure présente ne dit rien d'un repas d'hier : un dîner
   * rejoué à 8 h partait en petit-déjeuner, à 08:00. Hors du brouillon : c'est un appui en
   * cours, pas une saisie.
   */
  const [pending, setPending] = useState<Favorite | null>(null);
  // La ligne arrivée par un scan, qui attend son poids — c'est là que va le focus.
  const [weighing, setWeighing] = useState<string | null>(null);

  /**
   * Tout remettre à zéro — c'est ce que « annuler » veut dire, à n'importe quelle étape.
   *
   * **Le brouillon part avec.** C'est le seul geste qui dit « je ne veux plus de cette
   * saisie » : fermer la feuille, lui, ne l'efface plus.
   *
   * `useCallback` sans dépendance, et ce n'est pas une optimisation : l'effet de reprise
   * appelle cette fonction et doit ne se rejouer qu'à l'ouverture. Une fonction recréée à
   * chaque rendu l'y ferait entrer à chaque frappe.
   */
  const reset = useCallback((keepDay = false): void => {
    setMode(null);
    // « Changer de mode » garde le jour : on change la façon de noter, pas le repas.
    // Partout ailleurs — enregistré, brouillon périmé — la feuille repart d'aujourd'hui.
    setValues((current) => (keepDay ? { ...EMPTY, day: current.day } : EMPTY));
    setEstimate(null);
    setProposed([]);
    setRows([]);
    setTotal(null);
    setTotalError(null);
    setError(null);
    setLaterOpen(false);
    setStep({ kind: 'form' });
    setWeighing(null);
    setAsking(false);
    setPending(null);
    clearDraft();
    live.current = false;
    generation.current += 1;
  }, []);

  /**
   * Remet à l'écran ce qui avait été rangé.
   *
   * Le total n'en est pas : il appartient aux lignes qui l'ont produit et se redemande
   * d'un appui. Le reprendre afficherait un calcul que personne n'a refait — exactement
   * ce que le reste de la feuille prend soin de jeter dès qu'une quantité bouge.
   */
  const resume = useCallback((draft: MealDraft): void => {
    setMode(draft.mode);
    setValues(draft.values);
    setRows(draft.rows);
    setStep({ kind: 'form' });
    setWeighing(null);
    setProposed(draft.proposed);
    setEstimate(draft.estimate);
    setTotal(null);
    setTotalError(null);
    setError(null);
    // Le repli n'est pas repris : `showLater` le rouvre de lui-même si la saisie reprise
    // porte l'une des deux valeurs. Retenir l'état du repli aurait ajouté un champ au
    // brouillon pour une information qui se déduit de ce qu'il contient déjà.
    setLaterOpen(false);
    live.current = true;
  }, []);

  /**
   * À l'ouverture : reprendre la saisie rangée, ou repartir du choix du mode.
   *
   * `open` seul en dépendance de fond — la feuille ne se relit **qu'en s'ouvrant**. `live`
   * dit si elle tient déjà cette saisie : dans ce cas on n'y touche pas, sans quoi chaque
   * réouverture écraserait le total, qui ne vit qu'en mémoire.
   */
  useEffect(() => {
    if (!open) return;
    const draft = readDraft();
    /*
     * Un code inconnu venu de « Noter » (`NUT-28`) : le repas composé, et une ligne à
     * décrire qui le porte. **Une saisie en cours n'est pas écrasée** — le projet n'a
     * aucune annulation : un plat composé en cours reçoit la ligne en plus ; une saisie
     * dans un autre mode est reprise telle quelle, et la notification dit que le code
     * attend.
     */
    if (initialBarcode !== undefined && !posted.current) {
      posted.current = true;
      const row = describedIngredient(initialBarcode);
      const base = live.current ? null : draft;
      if (base !== null && base.mode !== 'compose') {
        resume(base);
        notify(
          `Un repas était en cours de saisie : il est repris tel quel. Enregistre-le, puis rescanne le code ${initialBarcode}.`,
          'recover',
        );
        return;
      }
      const rows = base?.rows ?? [];
      const posting: MealDraft = {
        mode: 'compose',
        // Sans brouillon, la feuille vient d'être montée : ses valeurs sont celles du départ.
        values: base?.values ?? (initialDay === undefined ? EMPTY : { ...EMPTY, day: initialDay }),
        rows: rows.some((item) => item.barcode === initialBarcode) ? rows : [...rows, row],
        proposed: base?.proposed ?? [],
        estimate: base?.estimate ?? null,
        saved_at: Date.now(),
      };
      /* **Rangé tout de suite**, et pas au rendu suivant. Sans brouillon écrit, le
         passage suivant de cet effet — le double appel du mode strict, vu au banc, ou
         toute dépendance qui bouge avant l'écriture — tombait sur « rien de rangé, mais
         une saisie vivante » et remettait la feuille à zéro : elle s'ouvrait sur le
         choix du mode, sans la ligne ni le code. Aucun test ne l'avait vu. */
      writeDraft(posting);
      resume(posting);
      setWeighing(row.key);
      return;
    }
    if (draft === null) {
      // Rien, ou plus rien : passé le délai, la feuille repart à zéro même si elle n'a
      // jamais été démontée. Une seule règle vaut mieux que deux durées de vie.
      if (live.current) reset();
    } else if (!live.current) {
      // Le jour de l'adresse l'emporte sur celui du brouillon : le lien « hier » du matin
      // ouvre la feuille sur hier même si une saisie d'aujourd'hui traînait. Le segment le
      // montre, et un appui le change.
      resume(
        initialDay === undefined
          ? draft
          : { ...draft, values: { ...draft.values, day: initialDay } },
      );
    }
  }, [open, reset, resume, initialDay, initialBarcode, notify]);

  /**
   * À chaque frappe : ranger. Le délai court depuis la dernière, pas depuis l'ouverture.
   *
   * `writeDraft` efface de lui-même une saisie redevenue vide : effacer sa description
   * fait donc disparaître le brouillon, ce qui est bien ce que le geste dit.
   */
  useEffect(() => {
    if (!open || mode === null) return;
    writeDraft({ mode, values, rows, proposed, estimate, saved_at: Date.now() });
  }, [open, mode, values, rows, proposed, estimate]);

  /**
   * « Pas d'accord » — et l'action fait vraiment ce qu'elle dit.
   *
   * Les valeurs **encore proposées** sont vidées, celles que l'utilisateur a retouchées
   * restent : elles sont à lui. La provenance retombe sur `manual`, sans quoi le fichier
   * dirait « ai » sur un repas dont l'estimation a été refusée.
   */
  function reject(): void {
    setValues((current) => {
      const cleared = { ...current, source: 'manual' as const };
      for (const macro of proposed) cleared[macro] = '';
      return cleared;
    });
    setProposed([]);
    setEstimate(null);
    setAsking(false);
  }

  const suggest = useMutation({
    mutationFn: async () => {
      const ticket = generation.current;
      const result = await nutritionApi.analyze(values.comment);
      return { ticket, result };
    },
    onSuccess: ({ ticket, result }) => {
      if (ticket === generation.current) setEstimate(result);
    },
    onError: (caught: unknown) => {
      // Un refus de l'IA se dit et s'oublie : la saisie manuelle reste entière (`IA-07`).
      // Le refus de taille, lui, porte maintenant un code et une phrase française —
      // c'était un `413` nu, donc un échec sans message.
      notify(caught instanceof ApiError ? caught.message : 'Estimation impossible.', 'recover');
    },
  });

  /** Les champs une fois la proposition appliquée, et lesquels en viennent (`NUT-04`). */
  function merged(current: typeof values, result: MealEstimate) {
    const filled: Macro[] = [];
    const next = { ...current, source: 'ai' as const };
    for (const { key } of NUTRIENTS) {
      const value = result[key];
      if (value === null) continue;
      next[key] = fieldText(value);
      filled.push(key);
    }
    // La description ne remplace jamais celle qui a été tapée : c'est elle que le modèle
    // a lue, et la reformuler par-dessus n'apprendrait rien.
    if (result.comment !== null && current.comment.trim() === '') next.comment = result.comment;
    return { next, filled };
  }

  /** Applique la proposition aux champs, et retient lesquels en viennent. */
  function accept(result: MealEstimate): void {
    const { next, filled } = merged(values, result);
    setValues(next);
    setProposed(filled);
  }

  /**
   * Le total, demandé au serveur. **N'écrit rien** (`NUT-12`).
   *
   * Il se demandait d'un appui sur « Calculer le total ». L'appui était facultatif —
   * l'enregistrement recalcule de son côté — et c'était tout le problème : le seul
   * avertissement de la surface, « aucun ingrédient n'a de valeur pour 100 g », ne
   * s'affichait que si on avait pensé à le demander. Un plat enregistré sans cet appui
   * partait sans macros, et le journal écrivait « macros non renseignées » sans qu'aucun
   * écran n'ait prévenu (`NUT-24`).
   *
   * Il se calcule donc **tout seul**, au repos de la frappe. Ce qui motivait le bouton
   * tient toujours — une requête par caractère n'a aucun sens — et c'est le délai qui s'en
   * charge maintenant.
   *
   * L'échec ne va **pas** dans `error` : celui-là est l'erreur du formulaire, levée par un
   * geste d'enregistrement. Une requête que personne n'a demandée ne doit pas écrire en
   * tête du formulaire, sous le type du repas. Elle se dit là où le total s'affiche.
   */
  const computeTotal = useMutation({
    mutationFn: () => nutritionApi.compose(toLines(rows)),
    onSuccess: (result) => {
      setTotalError(null);
      setTotal(result);
    },
    onError: (caught: unknown) => {
      setTotal(null);
      setTotalError(
        caught instanceof ApiError ? caught.message : 'Le total n’a pas pu être calculé.',
      );
    },
  });

  const saveComposed = useMutation({
    mutationFn: () =>
      nutritionApi.createComposed({
        meal_type: typeOf(values),
        comment: values.comment.trim(),
        lines: toLines(rows),
        ...(values.day ? { day: values.day } : {}),
      }),
    onSuccess: () => {
      notify(
        `Repas composé enregistré. Ses ingrédients sont retenus.${elsewhere(values.day)}`,
        'effort',
      );
      reset();
      onSaved();
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiError ? caught : null);
    },
  });

  // Les valeurs à écrire passent en argument : « avec l'estimation » doit enregistrer les
  // champs fusionnés, pas l'état d'avant la fusion que la fermeture aurait capturé.
  const save = useMutation({
    mutationFn: (fields: typeof values) =>
      nutritionApi.create({ ...fields, meal_type: typeOf(fields) }),
    onSuccess: (_meal, fields) => {
      notify(`Repas enregistré.${elsewhere(fields.day)}`, 'effort');
      reset();
      onSaved();
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiError ? caught : null);
    },
  });

  /**
   * Rejouer un favori, au jour du segment (M-23). Il n'a pas de formulaire : l'appui
   * écrit, comme « rejouer » le faisait sur la page. Pour hier, l'appui qui écrit est celui
   * du type — voir `pending`.
   */
  const replay = useMutation({
    mutationFn: ({ favorite, mealType }: { favorite: Favorite; mealType?: string }) =>
      nutritionApi.replayFavorite(favorite.favorite_id, {
        ...(values.day ? { day: values.day } : {}),
        ...(mealType !== undefined ? { meal_type: mealType } : {}),
      }),
    onSuccess: (meal) => {
      notify(`« ${meal.comment ?? 'Repas'} » ajouté.${elsewhere(values.day)}`, 'effort');
      reset();
      onSaved();
    },
    onError: (caught: unknown) => {
      setError(caught instanceof ApiError ? caught : null);
    },
  });

  /**
   * Poser une ligne remplie, et le doigt sur le champ du poids.
   *
   * Trois gestes en plus de l'insertion, et chacun a sa raison :
   *
   * * **la ligne vierge est remplacée**, pas suivie. Laisser une ligne vide au-dessus de
   *   l'aliment qui arrive ferait un plat qui commence par du vide ;
   * * **le poids prend le focus** : c'est la seule chose qui reste à taper ;
   * * **le nom du plat est repris du premier aliment** s'il est encore vide. Un repas
   *   composé sans nom ne s'enregistre pas — et pour l'immense majorité des plats à un
   *   ingrédient, l'aliment *est* le repas. Ce n'est pas une valeur inventée : elle vient
   *   de ce qui a été choisi, elle est à l'écran, et elle se retape.
   *
   * Commun au scan (`NUT-13`) et au choix au catalogue (`NUT-23`) : deux copies de ces
   * trois gestes auraient divergé au premier ajustement.
   */
  function addRow(row: IngredientDraft, name: string): void {
    setRows((current) => [...current.filter((item) => !isBlank(item)), row]);
    setWeighing(row.key);
    // Le total appartient aux lignes qui l'ont produit.
    setTotal(null);
    setTotalError(null);
    setValues((current) =>
      current.comment.trim() === '' ? { ...current, comment: name } : current,
    );
    setStep({ kind: 'form' });
  }

  /** Un aliment repris du catalogue : sa ligne arrive avec ses cinq valeurs (`NUT-23`). */
  function addFromCatalogue(item: Ingredient): void {
    addRow(ingredientFromCatalogue(item), item.name);
  }

  /**
   * Une ligne vierge, à nommer et à peser : le vrac, un reste, un plat cuisiné.
   *
   * **Un seul appelant depuis que « Saisir à la main » a quitté l'étape de choix** : le
   * code-barres inconnu de `ScanStep`, qui promet cette suite dans son message. La
   * fonction reste parce que ce chemin-là reste — et c'est le dernier à mener à une ligne
   * manuelle depuis la feuille.
   */
  function addManual(barcode: string): void {
    // **Le code suit la ligne** (`NUT-28`) : elle devient une ligne à décrire, la seule
    // qui montre ses valeurs pour 100 g. Sans code — il n'en arrive pas d'autre chemin
    // aujourd'hui —, la ligne vierge d'avant.
    const row = barcode === '' ? emptyIngredient() : describedIngredient(barcode);
    setRows((current) => [...current.filter((item) => !isBlank(item)), row]);
    setWeighing(row.key);
    setStep({ kind: 'form' });
  }

  /** Ce qu'un produit scanné devient (`NUT-13`). */
  function addProduct(product: Product): void {
    addRow(ingredientFromProduct(product), product.name);
    // **Des champs vides sans un mot se lisent comme une panne.** Un produit qu'Open Food
    // Facts connaît sans ses macros est fréquent — les produits frais, les marques de
    // distributeur — et la ligne vaut d'être ajoutée quand même : elle dit ce qu'il y
    // avait dans l'assiette. Encore faut-il savoir qu'il n'y aura rien à totaliser.
    if (product.partial) {
      notify(
        'Open Food Facts ne connaît pas les valeurs pour 100 g de ce produit. Ses champs restent vides.',
        'load',
      );
    }
  }

  const setMacro = (name: Macro) => (value: string) => {
    setValues((current) => ({ ...current, [name]: value }));
    // Retoucher une proposition la fait sienne, et la marque disparaît.
    setProposed((current) => current.filter((macro) => macro !== name));
  };

  /** Revenir au formulaire — ce que « Retour » veut dire dans les deux surfaces. */
  function back(): void {
    setStep({ kind: 'form' });
  }

  /**
   * La ligne dont la fiche est ouverte.
   *
   * Retrouvée par sa clé à chaque rendu plutôt que copiée dans l'état : une ligne dont on
   * garderait une copie afficherait des valeurs d'avant si elle changeait sous la fiche.
   * `undefined` si elle a disparu — retirée depuis une autre surface, ou brouillon repris.
   */
  const inspected = step.kind === 'food' ? rows.find((row) => row.key === step.key) : undefined;

  /*
   * Le jour choisi, retrouvé parmi ceux que le serveur sert. Vide veut dire aujourd'hui.
   * Une date qui n'y est plus — un brouillon d'hier soir repris passé minuit — ne
   * désigne aucun segment et bloque l'enregistrement : la rabattre sur aujourd'hui
   * daterait le repas d'un jour que personne n'a choisi.
   */
  const today = days.find((day) => day.key === 'today');
  const chosenDay = values.day === '' ? today : days.find((day) => day.date === values.day);
  const staleDay = values.day !== '' && chosenDay === undefined;
  const pastDay = chosenDay !== undefined && chosenDay.key !== 'today' ? chosenDay : undefined;

  /** Ce que le message d'enregistrement ajoute quand le repas ne rejoint pas la journée. */
  function elsewhere(day: string): string {
    return days.find((item) => item.date === day)?.key === 'yesterday'
      ? ' Il compte pour hier : « Hier », au-dessus du journal, le montre.'
      : '';
  }

  /**
   * Le type à écrire : celui qu'on a choisi, sinon celui de l'heure — **aujourd'hui
   * seulement**. Pour hier, l'heure présente ne dit rien du repas, et ce qu'elle proposerait
   * deviendrait aussi son heure type : vide, l'enregistrement attend un choix.
   */
  function typeOf(fields: MealFormValues): string {
    if (fields.meal_type) return fields.meal_type;
    return fields.day === '' || days.find((item) => item.date === fields.day)?.key === 'today'
      ? suggested
      : '';
  }

  const sentence = estimate === null ? '' : estimateSentence(estimate);
  const composing = mode === 'compose';
  const lines = toLines(rows);
  // Un repas composé a besoin de son nom **et** d'au moins un ingrédient pesé : sans le
  // premier il arriverait au journal sans rien pour le reconnaître, sans le second il n'y
  // aurait rien à composer.
  const nothingWritten = composing
    ? values.comment.trim() === '' || lines.length === 0
    : values.comment.trim() === '';
  const mealType = typeOf(values);
  const nothingToLog = staleDay || mealType === '' || nothingWritten;
  const nothingToEstimate = values.comment.trim() === '';
  // `computeTotal` n'en fait **pas** partie : il part tout seul au repos de la frappe, et
  // l'inclure ferait clignoter l'action principale à chaque gramme tapé. L'enregistrement
  // ne dépend pas de lui — il recalcule le total de son côté.
  // L'estimation en vol ne bloque plus l'enregistrement : 25 s de bouton grisé, même
  // pour des valeurs tapées à la main, et sans moyen d'en sortir (audit, fiche 16).
  const busy = save.isPending || saveComposed.isPending;
  /** Une estimation lisible, ni appliquée ni écartée : l'enregistrer sans le dire la perdrait. */
  const waiting =
    estimate !== null && estimate.readable && !estimate.empty && proposed.length === 0;
  /*
   * Le repli des deux dernières valeurs s'ouvre à la demande **ou** dès qu'il a quelque
   * chose à montrer. La seconde condition n'est pas un confort : une valeur remplie et
   * cachée partirait au serveur sans jamais avoir été à l'écran.
   */
  const showLater =
    laterOpen ||
    LATER.some((key) => values[key].trim() !== '' || proposed.includes(key)) ||
    LATER.some((key) => error?.messageFor(key) !== undefined);

  /*
   * Le total, au repos de la frappe (`NUT-24`).
   *
   * La **signature** des lignes en dépendance, et non le tableau : `toLines` en rend un
   * neuf à chaque rendu, et l'effet repartirait en boucle. Ce qui doit relancer le calcul
   * est le contenu — un gramme changé, un aliment retiré —, pas l'identité de l'objet.
   *
   * `fire` est une référence, comme la boucle de `ScanStep` avec son `onFound` : l'effet
   * ne se rejoue qu'au changement des lignes, et lirait sinon la mutation du premier
   * rendu.
   */
  const signature = JSON.stringify(lines);
  const fire = useRef<() => void>(() => undefined);
  useEffect(() => {
    fire.current = () => {
      computeTotal.mutate();
    };
  });

  useEffect(() => {
    // Sans ligne complète, il n'y a rien à totaliser — et rien à dire non plus : c'est
    // l'état vide du tableau qui parle, pas un total à zéro.
    if (!composing || signature === '[]') return;
    const timer = window.setTimeout(() => {
      fire.current();
    }, TOTAL_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [composing, signature]);

  return (
    <Sheet
      open={open}
      /* Fermer ne jette plus la saisie : les quatre portes de sortie de `Sheet` sont aussi
         celles d'un pouce qui dérape, ou d'un appel qui arrive. Le brouillon la garde
         quinze minutes ; « Changer de mode » reste le geste qui l'efface vraiment. */
      onClose={onClose}
      /* **Le choix du mode suit son contenu, les cinq autres pas tiennent la hauteur
         commune** (`UI-07`).

         C'est cette feuille qui a motivé la hauteur tenue de `Sheet` : le choix du mode,
         le formulaire, l'ajout d'un aliment, le scan et la fiche d'un produit donnaient
         cinq hauteurs, et la recherche du catalogue en donnait une par frappe. Le premier
         pas est l'exception parce qu'il est le seul dont le contenu **ne change pas** —
         trois lignes, rien qui entre ni sorte — et qu'à 86dvh elles flottaient au-dessus
         de 380 px de vide. Le saut qui suit est provoqué par l'appui sur un mode : on
         change d'écran, on ne subit pas une secousse.

         `mode === null` et non l'étape : quand aucun mode n'est choisi, aucune des trois
         surfaces qui prennent la feuille n'est atteignable — elles s'ouvrent depuis le
         formulaire du mode composé. */
      fit={mode === null}
      /* Le titre suit la surface — une feuille qui garde son titre ne dit pas où l'on
         est — et la fiche prend le nom de son aliment. Le répéter en tête de la fiche
         aurait écrit deux fois « Nutella » à deux centimètres d'écart. */
      title={
        step.kind === 'pick'
          ? 'Ajouter un aliment'
          : step.kind === 'scan'
            ? 'Scanner un aliment'
            : (inspected?.name ?? 'Ajouter un repas')
      }
      lede={
        mode === null
          ? `${pastDay !== undefined ? `Pour ${pastDay.label.toLowerCase()}, ${longDate(pastDay.date)}. ` : ''}${
              // Un favori écrit dès l'appui : « rien n'est enregistré avant ta validation »
              // serait faux à côté de lui.
              favorites.length > 0
                ? pastDay !== undefined
                  ? 'Un favori s’ajoute avec son type. Sinon, comment veux-tu le noter ?'
                  : 'Un favori s’ajoute d’un appui. Sinon, comment veux-tu le noter ?'
                : pastDay !== undefined
                  ? 'Comment veux-tu le noter ?'
                  : 'Comment veux-tu le noter ? Rien n’est enregistré avant ta validation.'
            }`
          : step.kind === 'pick'
            ? 'Dans ton catalogue, ou par son code-barres.'
            : step.kind === 'scan'
              ? 'Le code-barres suffit. Rien n’est enregistré avant ta validation.'
              : undefined
      }
    >
      {step.kind === 'pick' ? (
        <PickStep
          catalogue={ingredients}
          onPick={addFromCatalogue}
          // Un code lu par le viseur réduit arrive exactement là où arrive un code lu par
          // le viseur plein : c'est le même geste, vu de plus ou moins près.
          onProduct={addProduct}
          onScan={() => {
            setStep({ kind: 'scan' });
          }}
          onBack={back}
        />
      ) : step.kind === 'scan' ? (
        <ScanStep
          onFound={addProduct}
          onBack={back}
          // « Ajouter cet aliment à la main » depuis un code inconnu : la ligne arrive
          // vierge, comme depuis l'étape de choix.
          onManual={addManual}
        />
      ) : inspected !== undefined ? (
        <FoodDetail row={inspected} onBack={back} />
      ) : mode === null ? (
        <div className={styles.modes}>
          {error !== null && (
            <p className={styles.error} role="alert">
              {error.message}
            </p>
          )}

          {/* Le jour et les favoris tiennent ensemble, au-dessus des modes : ils disent
           *quand* et *quoi*, les modes disent *comment*. */}
          <div className={styles.modesHead}>
            {days.length > 1 && (
              <DayField
                days={days}
                value={values.day}
                stale={staleDay}
                onChange={(day) => {
                  setValues((current) => ({ ...current, day }));
                  // Le type demandé l'était pour un autre jour.
                  setPending(null);
                }}
              />
            )}

            {pending !== null && pastDay !== undefined ? (
              /* Le type, demandé **avant** d'écrire : il pose aussi l'heure type du repas
                 (M-01). Les types servis, en pastilles — la même bande que les favoris, à
                 la même place, pour qu'un appui suive l'autre sans chercher. */
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Quel repas ?</span>
                <p className={styles.note}>
                  « {pending.name} », pour {pastDay.label.toLowerCase()} : son type pose son heure
                  habituelle.
                </p>
                <ChipStrip label={`Type de « ${pending.name} »`}>
                  {types.map((type) => (
                    <Chip
                      key={type}
                      aria-label={`${type} : ajouter « ${pending.name} » pour ${pastDay.label.toLowerCase()}`}
                      disabled={replay.isPending}
                      onClick={() => {
                        setError(null);
                        replay.mutate({ favorite: pending, mealType: type });
                      }}
                    >
                      {type}
                    </Chip>
                  ))}
                </ChipStrip>
                <Button
                  variant="quiet"
                  disabled={replay.isPending}
                  onClick={() => {
                    setPending(null);
                  }}
                >
                  Un autre favori
                </Button>
              </div>
            ) : favorites.length > 0 ? (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Favoris</span>
                <ChipStrip label="Favoris">
                  {favorites.map((favorite) => (
                    <Chip
                      key={favorite.favorite_id}
                      aria-label={
                        pastDay !== undefined
                          ? `Ajouter « ${favorite.name} » pour ${pastDay.label.toLowerCase()} : choisir son type`
                          : `Ajouter « ${favorite.name} »`
                      }
                      disabled={replay.isPending || staleDay}
                      onClick={() => {
                        setError(null);
                        // Aujourd'hui, l'heure présente propose le type, et l'appui écrit.
                        // Un autre jour, il demande d'abord lequel.
                        if (pastDay !== undefined) {
                          setPending(favorite);
                          return;
                        }
                        replay.mutate({ favorite });
                      }}
                    >
                      {favorite.name}
                    </Chip>
                  ))}
                </ChipStrip>
              </div>
            ) : null}
          </div>

          {MODES.filter((item) => ai.enabled || OFFLINE_MODES.includes(item.value)).map((item) => (
            /* `SheetRow` : c'est la ligne que la charte réserve aux feuilles — pleine
               largeur, `--tap-lg`, libellé à gauche et indice à droite. L'indice y est
               une phrase, et le rendre en chasse fixe — comme le faisait l'ancien
               `LogButton`, dont l'indice était une **mesure** rappelée — la faisait
               passer pour un relevé. */
            <SheetRow
              key={item.value}
              label={item.label}
              hint={item.hint}
              // L'indice explique le choix, il ne le décrit pas : il passe donc sous le
              // libellé — à droite il lui prenait la moitié de la largeur — et quitte le
              // nom accessible, qu'il rallongeait d'une phrase entendue à chaque entrée.
              hintExplains
              onClick={() => {
                // Le refus d'un favori ne suit pas dans le formulaire d'un autre geste.
                setError(null);
                setMode(item.value);
                live.current = true;
              }}
            />
          ))}
          {/* Sans clé, les trois premiers modes n'ont rien à proposer : ils ne sont pas
              affichés grisés, ils ne sont pas affichés (`IA-07`). L'écran dit pourquoi
              plutôt que de laisser trois portes fermées. */}
          {!ai.enabled && <p className={styles.note}>{ai.message}</p>}
        </div>
      ) : (
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            if (composing) {
              saveComposed.mutate();
              return;
            }
            // Audit du 17 octobre, fiche 15 : l'estimation affichée mais pas acceptée
            // disparaissait à l'enregistrement, et le repas partait « macros non
            // renseignées ». Conforme à « une proposition n'est pas une mesure » — mais
            // sans le dire. On demande.
            if (waiting) {
              setAsking(true);
              return;
            }
            save.mutate(values);
          }}
          noValidate
        >
          {error !== null && (
            <p className={styles.error} role="alert">
              {error.message}
            </p>
          )}

          {days.length > 1 && (
            <DayField
              days={days}
              value={values.day}
              stale={staleDay}
              explain
              error={error?.messageFor('day')}
              onChange={(day) => {
                setValues((current) => ({ ...current, day }));
              }}
            />
          )}

          <div className={styles.field}>
            <label htmlFor="meal-type">Type</label>
            <select
              id="meal-type"
              className={styles.select}
              value={mealType}
              onChange={(event) => {
                setValues((current) => ({ ...current, meal_type: event.target.value }));
              }}
            >
              {/* Pour hier, rien n'est présélectionné : le type de l'heure présente serait
                  une valeur devinée, et il poserait aussi l'heure du repas. */}
              {mealType === '' && (
                <option value="" disabled>
                  Choisir le repas
                </option>
              )}
              {types.map((type) => (
                <option value={type} key={type}>
                  {type}
                </option>
              ))}
            </select>
            {mealType === '' && !staleDay && (
              <p className={styles.note}>
                Pour {pastDay?.label.toLowerCase() ?? 'ce jour'}, dis de quel repas il s’agit : son
                heure habituelle en dépend.
              </p>
            )}
          </div>

          {(wantsText(mode) || mode === 'manuel' || composing) && (
            <Field
              label={composing ? 'Nom du plat' : 'Description'}
              placeholder={composing ? 'bowl poulet riz' : 'poulet, riz, brocolis'}
              value={values.comment}
              error={error?.messageFor('comment')}
              onChange={(event) => {
                setValues((current) => ({ ...current, comment: event.target.value }));
              }}
            />
          )}

          {composing && (
            <>
              <IngredientTable
                rows={rows}
                catalogue={ingredients}
                weighing={weighing}
                onPick={() => {
                  setStep({ kind: 'pick' });
                }}
                onInspect={(key) => {
                  setStep({ kind: 'food', key });
                }}
                onChange={(next) => {
                  // **Le nom du plat suit le premier aliment** tant qu'on ne l'a pas changé
                  // (`NUT-24`, tenu à la frappe depuis `NUT-28`). Il n'était repris qu'à
                  // l'ajout d'une ligne — or une ligne à décrire arrive sans nom, et le plat
                  // d'un seul produit demandait d'écrire le même mot deux fois.
                  const before = rows[0]?.name.trim() ?? '';
                  const after = next[0]?.name.trim() ?? '';
                  if (before !== after) {
                    setValues((current) => {
                      const dish = current.comment.trim();
                      return dish === '' || dish === before
                        ? { ...current, comment: after }
                        : current;
                    });
                  }
                  setRows(next);
                  // Le total appartient aux lignes qui l'ont produit : changer une
                  // quantité sans le jeter laisserait un chiffre d'un autre plat à
                  // l'écran, et c'est celui-là qu'on croirait enregistrer. Le nouveau
                  // part tout seul, une demi-seconde après la dernière frappe.
                  setTotal(null);
                  setTotalError(null);
                }}
              />

              {/* L'attente est **dessinée** et non écrite : elle occupe la place de ce qui
                  arrive. Seulement quand il y a de quoi totaliser — sinon le tableau vide
                  serait suivi d'un fantôme de total que rien ne viendrait remplir. */}
              {total === null && totalError === null && lines.length > 0 && <Skeleton lines={2} />}

              {totalError !== null && (
                <p className={styles.error} role="alert">
                  {totalError}
                </p>
              )}

              {total !== null && (
                <CompositionTotal
                  lines={total.lines}
                  calories={total.calories}
                  proteinG={total.protein_g}
                  addedSugarG={total.added_sugar_g}
                  saturatedFatG={total.saturated_fat_g}
                  fiberG={total.fiber_g}
                  empty={total.empty}
                />
              )}
            </>
          )}

          {wantsEstimate(mode) &&
            (estimate === null && suggest.isPending ? (
              // L'attente se dit, et s'abandonne : un bouton grisé seul pendant 25 s ne
              // disait ni ce qui se passait ni comment en sortir (fiche 16).
              <div className={styles.waiting} role="status">
                <p>Lecture de la description… Tu peux enregistrer sans attendre.</p>
                <Button
                  variant="quiet"
                  onClick={() => {
                    generation.current += 1;
                    suggest.reset();
                  }}
                >
                  Annuler l’estimation
                </Button>
              </div>
            ) : estimate === null ? (
              <Button
                variant="ghost"
                disabled={nothingToEstimate}
                onClick={() => {
                  suggest.mutate();
                }}
              >
                Estimer les macros
              </Button>
            ) : (
              <AiBlock
                tag={proposed.length > 0 ? 'Estimation appliquée' : 'Estimation'}
                actions={
                  proposed.length > 0 || estimate.empty || !estimate.readable ? (
                    <Button variant="quiet" onClick={reject}>
                      Pas d&apos;accord
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="primary"
                        onClick={() => {
                          accept(estimate);
                        }}
                      >
                        Utiliser ces valeurs
                      </Button>
                      <Button variant="quiet" onClick={reject}>
                        Pas d&apos;accord
                      </Button>
                    </>
                  )
                }
              >
                {!estimate.readable ? (
                  <p>
                    Le modèle ne reconnaît pas de repas là-dedans. Les macros restent à saisir — ou
                    à laisser vides.
                  </p>
                ) : estimate.empty ? (
                  <p>
                    Le modèle n&apos;a rien su estimer. Rien n&apos;a été rempli : mieux vaut un
                    champ vide qu&apos;un chiffre inventé.
                  </p>
                ) : proposed.length > 0 ? (
                  <p>
                    Les champs en pointillé viennent de l&apos;estimation. Corrige-les au doigt : ce
                    que tu retouches devient ta valeur.
                  </p>
                ) : (
                  <p>
                    Ce repas contiendrait <strong>{sentence}</strong>. C&apos;est une estimation,
                    pas une mesure — rien n&apos;est enregistré avant ta validation.
                  </p>
                )}
              </AiBlock>
            ))}

          {/* Pas-à-pas et non champs libres : une valeur proposée doit pouvoir se corriger
              au pouce, sinon elle sera adoptée telle quelle faute de pouvoir la retoucher.

              **Absents du mode composé** : les macros y viennent du calcul du serveur, et
              trois champs modifiables à côté d'un total calculé laisseraient croire qu'on
              peut avoir les deux — alors que l'enregistrement recalcule. */}
          <div className={cx(styles.triple, composing && styles.hidden)} hidden={composing}>
            {NUTRIENTS.filter((nutrient) => !LATER.includes(nutrient.key)).map((nutrient) => (
              <Stepper
                key={nutrient.key}
                label={nutrient.field}
                inputMode={nutrient.inputMode}
                value={values[nutrient.key]}
                onChange={setMacro(nutrient.key)}
                step={nutrient.step}
                min={0}
                proposed={proposed.includes(nutrient.key)}
                error={error?.messageFor(nutrient.key)}
              />
            ))}
          </div>

          {/* Les deux dernières, repliées tant qu'elles sont vides. Le repli **s'ouvre de
              lui-même** dès que l'une porte quelque chose — une estimation acceptée, une
              saisie reprise, une erreur du serveur : une valeur qui part doit être à
              l'écran, et une valeur cachée qu'on enregistre serait exactement ce que le
              dépôt refuse. */}
          {!composing &&
            (showLater ? (
              <div className={styles.triple}>
                {NUTRIENTS.filter((nutrient) => LATER.includes(nutrient.key)).map((nutrient) => (
                  <Stepper
                    key={nutrient.key}
                    label={nutrient.field}
                    inputMode={nutrient.inputMode}
                    value={values[nutrient.key]}
                    onChange={setMacro(nutrient.key)}
                    step={nutrient.step}
                    min={0}
                    proposed={proposed.includes(nutrient.key)}
                    error={error?.messageFor(nutrient.key)}
                  />
                ))}
              </div>
            ) : (
              <Button
                variant="quiet"
                onClick={() => {
                  setLaterOpen(true);
                }}
              >
                Plus de valeurs — AG saturés, fibres
              </Button>
            ))}

          {asking && estimate !== null && (
            <div className={styles.waiting} role="alert">
              <p>Une estimation attend encore ta réponse. Enregistrer le repas avec, ou sans ?</p>
              <div className="row">
                <Button
                  variant="primary"
                  busy={save.isPending}
                  onClick={() => {
                    const { next, filled } = merged(values, estimate);
                    setProposed(filled);
                    save.mutate(next);
                  }}
                >
                  Avec l’estimation
                </Button>
                <Button
                  variant="quiet"
                  disabled={save.isPending}
                  onClick={() => {
                    save.mutate(values);
                  }}
                >
                  Sans
                </Button>
              </div>
            </div>
          )}

          <div className={styles.sheetCommit}>
            <Button
              type="submit"
              variant="primary"
              className={cx(styles.commit)}
              busy={save.isPending || saveComposed.isPending}
              disabled={nothingToLog || busy}
            >
              Enregistrer le repas
            </Button>
            {/* Revenir en arrière **à n'importe quelle étape**, sans rien écrire. Un
                second appui — la poignée, le voile, Échap — ferme la feuille entière. */}
            <Button
              variant="quiet"
              disabled={save.isPending}
              onClick={() => {
                reset(true);
              }}
            >
              Changer de mode
            </Button>
          </div>

          {nothingWritten && !staleDay && (
            <p className={styles.empty}>
              {composing
                ? 'Un nom de plat et un ingrédient pesé suffisent. Les valeurs pour 100 g peuvent rester vides.'
                : 'Une description suffit. Les macros peuvent attendre.'}
            </p>
          )}
        </form>
      )}
    </Sheet>
  );
}

/**
 * Le jour du repas : les deux jours servis, en segment (M-01).
 *
 * Aux deux étapes de la feuille depuis M-23 — l'étape du mode, pour les favoris, et le
 * formulaire —, sur le même état. **Aujourd'hui s'écrit vide** : sans jour, le serveur
 * date à l'instant même. Une date qui n'est plus servie ne sélectionne rien et le dit.
 */
function DayField({
  days,
  value,
  stale,
  explain = false,
  error,
  onChange,
}: {
  days: readonly MealDay[];
  /** Vide pour aujourd'hui, sinon une date servie. */
  value: string;
  stale: boolean;
  /** Dire l'heure posée sur un jour passé — au formulaire seulement : l'étape du mode
   *  porte déjà la date dans sa phrase d'en-tête. */
  explain?: boolean;
  error?: string | undefined;
  onChange: (day: string) => void;
}) {
  const today = days.find((day) => day.key === 'today');
  const chosen = value === '' ? today : days.find((day) => day.date === value);
  const past = chosen !== undefined && chosen.key !== 'today' ? chosen : undefined;
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>Jour</span>
      <Segmented
        options={days.map((day) => ({ value: day.date, label: day.label }))}
        value={chosen?.date ?? ''}
        onChange={(next) => {
          onChange(next === today?.date ? '' : next);
        }}
        label="Jour du repas"
      />
      {stale ? (
        <p className={styles.error} role="alert">
          Ce brouillon était pour le {longDate(value)}, qui ne se note plus. Choisis aujourd’hui ou
          hier.
        </p>
      ) : explain && past !== undefined ? (
        <p className={styles.note}>
          {longDate(past.date)}, à l’heure habituelle de ce type de repas — elle n’est pas demandée.
        </p>
      ) : null}
      {error !== undefined && !stale && <p className={styles.error}>{error}</p>}
    </div>
  );
}

/**
 * Écrit un nombre pour un champ de saisie.
 *
 * Sans séparateur de milliers, contrairement à `num` : ce texte repart vers le serveur, et
 * la virgule décimale fait partie du contrat (`ACT-01`).
 */
function fieldText(value: number): string {
  return String(value).replace('.', ',');
}
